import { logger } from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import { useConfirm } from './ConfirmModal';
import './AdminEventsManager.css';

interface AdminEvent {
  id: number;
  name: string;
  description: string;
  eventType: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
  regionName?: string;
  gatheringType?: string;
  quantityMultiplier: number;
  xpMultiplier: number;
  rarityBonus: number;
  specialRewards?: string;
  createdAt: string;
  updatedAt: string;
  worldTile?: {
    id: number;
    name: string;
    tileType: string;
  };
  status: string;
  duration: string;
}

interface EventType {
  value: string;
  label: string;
  description: string;
}

interface CreateEventData {
  name: string;
  description: string;
  eventType: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
  regionName?: string;
  gatheringType?: string;
  quantityMultiplier: number;
  xpMultiplier: number;
  rarityBonus: number;
  specialRewards?: string;
}

const AdminEventsManager: React.FC = () => {
  const { handleAuthError } = useAuth();
  const { confirm, confirmDialog } = useConfirm();
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<AdminEvent | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [filterType, setFilterType] = useState<string>('');
  const [sortBy, setSortBy] = useState('CreatedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Form data for create/edit
  const [formData, setFormData] = useState<CreateEventData>({
    name: '',
    description: '',
    eventType: 'BonusResources',
    startTime: new Date().toISOString().slice(0, 16),
    endTime: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString().slice(0, 16),
    isActive: true,
    regionName: '',
    gatheringType: '',
    quantityMultiplier: 1.5,
    xpMultiplier: 1.2,
    rarityBonus: 5,
    specialRewards: ''
  });

  const fetchEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy,
        sortOrder
      });

      if (filterActive !== null) {
        queryParams.append('isActive', filterActive.toString());
      }
      if (filterType) {
        queryParams.append('eventType', filterType);
      }

      const response = await makeAuthenticatedRequest(`/admin/events?${queryParams}`);

      if (response.ok) {
        const data = await response.json();
        setEvents(data.events);
      } else {
        setError('Failed to load events');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error loading events');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, filterActive, filterType, sortBy, sortOrder, handleAuthError]);

  const fetchEventTypes = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/admin/events/types');
      if (response.ok) {
        const data = await response.json();
        setEventTypes(data.eventTypes);
      }
    } catch (err) {
      logger.warn('Failed to load event types:', err);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchEventTypes();
  }, [fetchEvents, fetchEventTypes]);

  const handleCreateEvent = async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/admin/events', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowCreateModal(false);
        setFormData({
          name: '',
          description: '',
          eventType: 'BonusResources',
          startTime: new Date().toISOString().slice(0, 16),
          endTime: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString().slice(0, 16),
          isActive: true,
          regionName: '',
          gatheringType: '',
          quantityMultiplier: 1.5,
          xpMultiplier: 1.2,
          rarityBonus: 5,
          specialRewards: ''
        });
        await fetchEvents();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to create event');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error creating event');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleEventStatus = async (eventId: number, currentStatus: boolean) => {
    try {
      const response = await makeAuthenticatedRequest(`/admin/events/${eventId}`, {
        method: 'PUT',
        body: JSON.stringify({ isActive: !currentStatus })
      });

      if (response.ok) {
        await fetchEvents();
        setError(null);
      } else {
        setError('Failed to update event status');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error updating event');
    }
  };

  const handleDeleteEvent = async (eventId: number) => {
    const confirmed = await confirm({
      title: 'Delete Event',
      message: 'Are you sure you want to delete this event? This action cannot be undone.',
      confirmText: 'Delete',
      danger: true,
    });
    if (!confirmed) {
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`/admin/events/${eventId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchEvents();
        setError(null);
      } else {
        setError('Failed to delete event');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error deleting event');
    }
  };

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (duration: string): string => {
    try {
      const match = duration.match(/(\d+):(\d+):(\d+)/);
      if (match) {
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        return `${hours}h ${minutes}m`;
      }
      return duration;
    } catch {
      return 'Unknown';
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'active': return 'status-active';
      case 'scheduled': return 'status-scheduled';
      case 'expired': return 'status-expired';
      case 'inactive': return 'status-inactive';
      default: return 'status-unknown';
    }
  };

  const getEventTypeColor = (eventType: string): string => {
    switch (eventType) {
      case 'BonusResources': return '#3498db';
      case 'DoubleXP': return '#e74c3c';
      case 'RareMaterials': return '#9b59b6';
      case 'CommunityBoost': return '#27ae60';
      case 'MaterialRush': return '#f39c12';
      case 'LegendaryHunt': return '#e67e22';
      default: return '#95a5a6';
    }
  };

  if (isLoading && events.length === 0) {
    return (
      <div className="events-manager">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="events-manager">
      {confirmDialog}
      <div className="events-header">
        <div className="events-title">
          <h2>🎪 Dynamic Events Management</h2>
          <p>Create and manage gathering events to boost player engagement</p>
        </div>
        <button 
          className="create-event-button"
          onClick={() => setShowCreateModal(true)}
        >
          ✨ Create Event
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Filters */}
      <div className="events-filters">
        <div className="filter-group">
          <label>Status:</label>
          <select 
            value={filterActive === null ? '' : filterActive.toString()} 
            onChange={(e) => setFilterActive(e.target.value === '' ? null : e.target.value === 'true')}
          >
            <option value="">All Events</option>
            <option value="true">Active Only</option>
            <option value="false">Inactive Only</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Type:</label>
          <select 
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All Types</option>
            {eventTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Sort by:</label>
          <select 
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="CreatedAt">Created Date</option>
            <option value="StartTime">Start Time</option>
            <option value="Name">Name</option>
            <option value="EventType">Type</option>
          </select>
          <button 
            className={`sort-order ${sortOrder === 'desc' ? 'desc' : 'asc'}`}
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        <button className="refresh-button" onClick={fetchEvents}>
          🔄 Refresh
        </button>
      </div>

      {/* Events Table */}
      <div className="events-table-container">
        <table className="events-table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Type</th>
              <th>Status</th>
              <th>Schedule</th>
              <th>Bonuses</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map(event => (
              <tr key={event.id} className="event-row">
                <td className="event-info">
                  <div className="event-name">{event.name}</div>
                  <div className="event-description">{event.description}</div>
                  {event.regionName && <div className="event-region">🌍 {event.regionName}</div>}
                  {event.gatheringType && <div className="event-gathering">🛠️ {event.gatheringType}</div>}
                </td>
                <td>
                  <span 
                    className="event-type-badge"
                    style={{ backgroundColor: getEventTypeColor(event.eventType) }}
                  >
                    {event.eventType}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${getStatusBadgeClass(event.status)}`}>
                    {event.status}
                  </span>
                </td>
                <td className="event-schedule">
                  <div>Start: {formatDateTime(event.startTime)}</div>
                  <div>End: {formatDateTime(event.endTime)}</div>
                  <div className="duration">Duration: {formatDuration(event.duration)}</div>
                </td>
                <td className="event-bonuses">
                  {event.quantityMultiplier > 1 && <div>📦 {event.quantityMultiplier.toFixed(1)}x Materials</div>}
                  {event.xpMultiplier > 1 && <div>⭐ {event.xpMultiplier.toFixed(1)}x XP</div>}
                  {event.rarityBonus > 0 && <div>💎 +{event.rarityBonus}% Rarity</div>}
                </td>
                <td className="event-actions">
                  <button 
                    className={`toggle-button ${event.isActive ? 'active' : 'inactive'}`}
                    onClick={() => handleToggleEventStatus(event.id, event.isActive)}
                  >
                    {event.isActive ? 'Disable' : 'Enable'}
                  </button>
                  <button 
                    className="view-button"
                    onClick={() => setSelectedEvent(event)}
                  >
                    👁️ View
                  </button>
                  <button 
                    className="delete-button"
                    onClick={() => handleDeleteEvent(event.id)}
                  >
                    🗑️ Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {events.length === 0 && (
          <div className="empty-state">
            <h3>No events found</h3>
            <p>Create your first gathering event to engage players!</p>
            <button 
              className="create-event-button"
              onClick={() => setShowCreateModal(true)}
            >
              ✨ Create First Event
            </button>
          </div>
        )}
      </div>

      {/* Create Event Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>✨ Create New Event</h3>
              <button onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Event Name *</label>
                  <input 
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Summer Resource Bonanza"
                  />
                </div>

                <div className="form-group">
                  <label>Event Type *</label>
                  <select 
                    value={formData.eventType}
                    onChange={(e) => setFormData({...formData, eventType: e.target.value})}
                  >
                    {eventTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label} - {type.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe what makes this event special..."
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label>Start Time *</label>
                  <input 
                    type="datetime-local"
                    value={formData.startTime}
                    onChange={(e) => setFormData({...formData, startTime: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>End Time *</label>
                  <input 
                    type="datetime-local"
                    value={formData.endTime}
                    onChange={(e) => setFormData({...formData, endTime: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Region (optional)</label>
                  <select 
                    value={formData.regionName || ''}
                    onChange={(e) => setFormData({...formData, regionName: e.target.value || undefined})}
                  >
                    <option value="">All Regions</option>
                    <option value="Safe Haven Valley">Safe Haven Valley</option>
                    <option value="Mystic Woodlands">Mystic Woodlands</option>
                    <option value="Shadowlands">Shadowlands</option>
                    <option value="Forbidden Peaks">Forbidden Peaks</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Gathering Type (optional)</label>
                  <select 
                    value={formData.gatheringType || ''}
                    onChange={(e) => setFormData({...formData, gatheringType: e.target.value || undefined})}
                  >
                    <option value="">All Types</option>
                    <option value="Wood">Wood</option>
                    <option value="Ore">Ore</option>
                    <option value="Herbs">Herbs</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Quantity Multiplier</label>
                  <input 
                    type="number"
                    step="0.1"
                    min="1"
                    max="5"
                    value={formData.quantityMultiplier}
                    onChange={(e) => setFormData({...formData, quantityMultiplier: parseFloat(e.target.value)})}
                  />
                </div>

                <div className="form-group">
                  <label>XP Multiplier</label>
                  <input 
                    type="number"
                    step="0.1"
                    min="1"
                    max="5"
                    value={formData.xpMultiplier}
                    onChange={(e) => setFormData({...formData, xpMultiplier: parseFloat(e.target.value)})}
                  />
                </div>

                <div className="form-group">
                  <label>Rarity Bonus (%)</label>
                  <input 
                    type="number"
                    min="0"
                    max="100"
                    value={formData.rarityBonus}
                    onChange={(e) => setFormData({...formData, rarityBonus: parseInt(e.target.value)})}
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input 
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                    />
                    Start Active
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
              <button 
                className="create-button"
                onClick={handleCreateEvent}
                disabled={!formData.name || !formData.eventType}
              >
                ✨ Create Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Event Details Modal */}
      {selectedEvent && (
        <div className="modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📊 Event Details</h3>
              <button onClick={() => setSelectedEvent(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="event-details">
                <div className="detail-section">
                  <h4>Basic Information</h4>
                  <div className="detail-grid">
                    <div><strong>Name:</strong> {selectedEvent.name}</div>
                    <div><strong>Type:</strong> {selectedEvent.eventType}</div>
                    <div><strong>Status:</strong> <span className={getStatusBadgeClass(selectedEvent.status)}>{selectedEvent.status}</span></div>
                    <div><strong>Active:</strong> {selectedEvent.isActive ? '✅ Yes' : '❌ No'}</div>
                  </div>
                  <div className="description">
                    <strong>Description:</strong>
                    <p>{selectedEvent.description}</p>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Schedule</h4>
                  <div className="detail-grid">
                    <div><strong>Start:</strong> {formatDateTime(selectedEvent.startTime)}</div>
                    <div><strong>End:</strong> {formatDateTime(selectedEvent.endTime)}</div>
                    <div><strong>Duration:</strong> {formatDuration(selectedEvent.duration)}</div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Location & Targeting</h4>
                  <div className="detail-grid">
                    <div><strong>Region:</strong> {selectedEvent.regionName || 'All Regions'}</div>
                    <div><strong>Gathering Type:</strong> {selectedEvent.gatheringType || 'All Types'}</div>
                    {selectedEvent.worldTile && (
                      <div><strong>Specific Tile:</strong> {selectedEvent.worldTile.name} ({selectedEvent.worldTile.tileType})</div>
                    )}
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Bonuses & Rewards</h4>
                  <div className="bonuses-display">
                    <div className="bonus-item">
                      <span className="bonus-icon">📦</span>
                      <span>Material Quantity: {selectedEvent.quantityMultiplier.toFixed(1)}x</span>
                    </div>
                    <div className="bonus-item">
                      <span className="bonus-icon">⭐</span>
                      <span>Experience Points: {selectedEvent.xpMultiplier.toFixed(1)}x</span>
                    </div>
                    <div className="bonus-item">
                      <span className="bonus-icon">💎</span>
                      <span>Rarity Bonus: +{selectedEvent.rarityBonus}%</span>
                    </div>
                  </div>
                  {selectedEvent.specialRewards && (
                    <div>
                      <strong>Special Rewards:</strong>
                      <p>{selectedEvent.specialRewards}</p>
                    </div>
                  )}
                </div>

                <div className="detail-section">
                  <h4>System Information</h4>
                  <div className="detail-grid">
                    <div><strong>Event ID:</strong> {selectedEvent.id}</div>
                    <div><strong>Created:</strong> {formatDateTime(selectedEvent.createdAt)}</div>
                    <div><strong>Last Updated:</strong> {formatDateTime(selectedEvent.updatedAt)}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="admin-close-btn"
                onClick={() => setSelectedEvent(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEventsManager;